import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { InfoIcon, Warning } from '@phosphor-icons/react'
import ReactTooltip from 'react-tooltip'
import { useTranslation } from 'react-i18next'

import { getDate, getMediaUrl } from '../helpers'
import { getAssetLink } from '../../../helpers/get-asset-link'
import AudioMessage from '../ChatSidebar/AudioMessage'
import AudioMessageRecord from '../ChatSidebar/AudioMessageRecord'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import ModalContainer from '../../ModalContainer/ModelContainer'
import PicturesModal from '../../Modal/PicturesModal'
import RequestMaintenanceForm from '../../Maintenance/RequestMaintenanceForm/RequestMaintenanceForm'
import API from '../../../api/api'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import { APP_COLORS } from '../../../helpers/constants'

const Message = ({ message, sendByUs, reSendSMS }) => {
  const { t } = useTranslation(['chat'])
  const [isConfirmPopupVisible, setConfirmPopupVisible] = useState(false)
  const [isMaintenanceRequestPopapVisible, setMaintenanceRequestPopapVisible] = useState(false)
  const [pictureToShow, setPictureToShow] = useState(null)
  const { displaySnackbar } = useSnackbar()

  const isImageAttachment = !!message.image && !!message.media?.contentType?.startsWith('image/')

  const onOpenPicture = async () => {
    let freshUrl = null
    try {
      freshUrl = await getMediaUrl(message.media)
    } catch (err) {
      console.error(err?.message)
    }
    if (!freshUrl) {
      displaySnackbar('Unable to refresh the image. It may no longer be available', true)
    }
    setPictureToShow(freshUrl || message.image)
  }

  const label = useMemo(() => {
    const dateAndUser = `${message.user.name} ${t('chat:message.atConnector')} ${getDate(message.createdAt)}, `
    let color = ''
    let type = ''
    let tip = ''

    const isError = message.messageStatus === 'error'

    if (message.isSMS && isError) {
      color = APP_COLORS.red500
      type = t('chat:message.smsNotSentError')
    } else if (message.fromAnnouncement) {
      color = APP_COLORS.green500
      type = t('chat:message.sentAsSmsAnnouncements')
      tip = t('chat:message.smsAnnouncementTip')
    } else if (message.isSMS && !isError) {
      color = APP_COLORS.green500
      type = t('chat:message.sentAsSms')
    } else {
      color = APP_COLORS.blue500
      type = t('chat:message.sentAsChat')
    }

    return { dateAndUser, color, type, tip }
  }, [message, t])

  return (
    <div className={sendByUs ? `message-wrapper right` : `message-wrapper left`}>
      <div className="avatar-container">
        <img src={getAssetLink({ path: 'chitown-avatar/avatar', file: message.user.avatar })} />
      </div>
      {message.type === 'text' ? (
        <div className="body-message">
          {message.isCall ? (
            <React.Fragment>
              {message.emergency && <Warning size={17} />}
              <AudioMessage status={message.messageStatus} text={message.text} />
              {message.recordingUrl && (
                <AudioMessageRecord
                  recordingUrl={message.recordingUrl}
                  transcriptionBegin={message.transcriptionBegin}
                  transcriptionUrl={message.transcriptionUrl}
                />
              )}
              {message.emergency && (
                <div className="emergecny-buttons-wrap">
                  <button
                    className="button button-black"
                    onClick={() => setMaintenanceRequestPopapVisible(true)}
                  >
                    {t('chat:message.createRequest')}
                  </button>
                  <button
                    className="button button-gray"
                    onClick={() => setConfirmPopupVisible(true)}
                  >
                    {t('chat:message.setAsNonEmergency')}
                  </button>
                </div>
              )}
            </React.Fragment>
          ) : (
            <div className="text">{message.text}</div>
          )}
          <div className="name-and-time">
            {label.dateAndUser}
            <span style={{ color: label.color }}>{label.type}</span>
            <span
              onClick={() => reSendSMS(message)}
              style={{ color: '#4A4A4A', textDecoration: 'underline', cursor: 'pointer' }}
            >
              {message.messageStatus === 'error' ? t('chat:message.sendItAgain') : null}
            </span>
            {label.tip ? (
              <div
                data-tip
                data-for="type-tip"
                style={{ position: 'absolute', top: -1.5, right: -24 }}
              >
                <InfoIcon size={20} />
                <ReactTooltip multiline id="type-tip" place="bottom" effect="solid">
                  {label.tip}
                </ReactTooltip>
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="body-message">
          <div className="image-container">
            <div
              className={`image ${isImageAttachment ? 'is-clickable' : ''}`}
              onClick={isImageAttachment ? onOpenPicture : undefined}
              style={{ backgroundImage: `url(${message.image})` }}
            />
          </div>
          <div className="name-and-time">
            {`${message.user.name} ${t('chat:message.atConnector')} ${getDate(message.createdAt)}, `}
            <span style={{ color: message.isSMS ? '#548671' : '#3468CD' }}>
              {t('chat:message.sentAsChat')}
            </span>
          </div>
        </div>
      )}
      <ConfirmPopup
        title={<span className="modal--confirm-title">{t('chat:message.setAsNonEmergency')}</span>}
        body={t('chat:message.nonEmergencyConfirmBody')}
        cancelText={t('common:actions.cancel')}
        confirmText={t('chat:message.yesSetAsNonEmergency')}
        isOpen={isConfirmPopupVisible}
        cancelCallback={() => setConfirmPopupVisible(false)}
        confirmCallback={() => {
          API.deleteChatMessage(message.id)
            .then(() => displaySnackbar(t('chat:message.nonEmergencySuccess')))
            .catch(() => displaySnackbar(t('chat:message.nonEmergencyError'), true))
            .finally(() => setConfirmPopupVisible(false))
        }}
        className="is-caution"
      />
      {pictureToShow && (
        <PicturesModal
          visible={true}
          useDirectLinks={true}
          pictures={[pictureToShow]}
          onCloseModal={() => setPictureToShow(null)}
        />
      )}
      {isMaintenanceRequestPopapVisible && (
        <ModalContainer
          headerTitle={t('chat:message.createMaintenanceRequest')}
          onClose={() => setMaintenanceRequestPopapVisible(false)}
        >
          <RequestMaintenanceForm
            voicemailChatMessageSid={message.id}
            toggleModal={() => setMaintenanceRequestPopapVisible((prev) => !prev)}
          />
        </ModalContainer>
      )}
    </div>
  )
}

Message.propTypes = {
  message: PropTypes.object,
  sendByUs: PropTypes.bool,
  reSendSMS: PropTypes.func,
}

export default Message
